import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import SitesMapTree from '../components/SitesMapTree';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import { WarehouseType } from 'graphql/types/WarehouseType';
import { SiteType } from 'graphql/types/SiteType';

// 预设的 20 种颜色
const PRESET_COLORS = [
    '#722ed1', // 紫色
    '#52c41a', // 绿色
    '#faad14', // 橙色
    '#f5222d', // 红色
    '#1890ff', // 蓝色
    '#13c2c2', // 青色
    '#eb2f96', // 粉色
    '#fa8c16', // 橙红色
    '#ff4d4f', // 珊瑚红
    '#a0d911', // 浅绿色
    '#fa541c', // 深橙色
    '#cf1322', // 深红色
    '#531dab', // 深紫色
    '#08979c', // 深青色
    '#c41d7f', // 深粉色
    '#d46b08', // 棕色
    '#0958d9', // 海军蓝
    '#389e0d', // 森林绿
    '#d4380d', // 砖红色
    '#9254de', // 薰衣草紫
];

// Google Maps API Key
const GOOGLE_MAPS_API_KEY = 'AIzaSyD8huYA1dL8uG9O1a77bnW3JtpTjUimjmc';

// 地图默认中心点（美国中部）
const defaultCenter = {
    lat: 39.8283,
    lng: -98.5795
};

// 地图配置选项
const mapOptions = {
    disableDefaultUI: false,
    zoomControl: true,
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: true,
};

// Marker 组件（简化版，无动画）
const SiteMarker: React.FC<{
    position: { lat: number; lng: number };
    color: string;
    isHub: boolean;
    title: string;
    zIndex: number;
    site: WarehouseType;
    onMarkerClick: (site: WarehouseType) => void;
    onMarkerLoad?: (marker: google.maps.Marker, siteId: number) => void;
}> = ({ position, color, isHub, title, zIndex, site, onMarkerClick, onMarkerLoad }) => {
    const baseSize = isHub ? { width: 32, height: 40 } : { width: 19, height: 24 };
    const baseAnchor = isHub ? { x: 16, y: 40 } : { x: 9.5, y: 24 };
    
    // 使用 onLoad 回调获取 marker 实例
    const handleMarkerLoad = (markerComponent: any) => {
        if (!onMarkerLoad || !site.id) return;
        
        // @react-google-maps/api 的 Marker 组件的 onLoad 回调接收的是 React 组件实例
        // 需要通过 .marker 属性获取底层的 google.maps.Marker 实例
        let marker: google.maps.Marker | null = null;
        
        if (markerComponent && markerComponent.marker) {
            marker = markerComponent.marker as google.maps.Marker;
        } else if (markerComponent instanceof google.maps.Marker) {
            // 如果直接是 Marker 实例
            marker = markerComponent;
        }
        
        if (marker) {
            onMarkerLoad(marker, site.id);
        }
    };
    
    // 创建 icon
    const icon = useMemo(() => {
        const svg = `
            <svg width="${baseSize.width}" height="${baseSize.height}" viewBox="0 0 32 40" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 0C7.163 0 0 7.163 0 16c0 11.5 16 24 16 24s16-12.5 16-24C32 7.163 24.837 0 16 0z" fill="${color}"/>
                <circle cx="16" cy="16" r="6" fill="white"/>
            </svg>
        `;
        
        return {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
            scaledSize: { width: baseSize.width, height: baseSize.height } as google.maps.Size,
            anchor: { x: baseAnchor.x, y: baseAnchor.y } as google.maps.Point,
        };
    }, [color, isHub, baseSize, baseAnchor]);
    
    return (
        <Marker
            onLoad={handleMarkerLoad}
            position={position}
            icon={icon}
            title={title}
            zIndex={zIndex}
            onClick={(e) => {
                // 从事件中获取 marker 实例
                if (e.domEvent && (e.domEvent.target as any).parentElement) {
                    const markerElement = (e.domEvent.target as any).parentElement;
                    // 尝试从 DOM 元素中找到 marker 实例
                    const marker = (markerElement as any).__marker__;
                    if (marker && onMarkerLoad) {
                        onMarkerLoad(marker, site.id || 0);
                    }
                }
                onMarkerClick(site);
            }}
        />
    );
};

const SiteMapPage: React.FC = () => {
    const [selectedWarehouseIds, setSelectedWarehouseIds] = useState<Set<string>>(new Set());
    const [selectedWarehouses, setSelectedWarehouses] = useState<WarehouseType[]>([]);
    const [allWarehouses, setAllWarehouses] = useState<WarehouseType[]>([]);
    const mapRef = useRef<google.maps.Map | null>(null);
    const [newlySelectedIds, setNewlySelectedIds] = useState<Set<string>>(new Set());
    const previousSelectedIdsRef = useRef<Set<string>>(new Set());
    const [selectedSite, setSelectedSite] = useState<WarehouseType | null>(null);
    const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
    const markerRefsRef = useRef<Map<number, google.maps.Marker>>(new Map());

    // 加载 Google Maps API
    const { isLoaded, loadError } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    });

    // 处理 checkbox 选择变化
    const handleCheckboxChange = useCallback((checkedWarehouseIds: Set<string>, warehouses: WarehouseType[]) => {
        setSelectedWarehouseIds(checkedWarehouseIds);
        setSelectedWarehouses(warehouses);
    }, []);

    // 处理所有 warehouse 数据变化（用于颜色分配）
    const handleWarehousesChange = useCallback((warehouses: WarehouseType[]) => {
        setAllWarehouses(warehouses);
    }, []);

    // 生成随机颜色
    const generateRandomColor = (): string => {
        const letters = '0123456789ABCDEF';
        let color = '#';
        for (let i = 0; i < 6; i++) {
            color += letters[Math.floor(Math.random() * 16)];
        }
        return color;
    };

    // 根据 hub ID 获取颜色
    const getColorForHub = useMemo(() => {
        // 获取所有 hub
        const hubs = allWarehouses.filter(w => w.siteType === SiteType.HUB);
        
        // 创建 hub ID 到颜色的映射
        const hubColorMap = new Map<number, string>();
        
        hubs.forEach((hub, index) => {
            if (hub.id) {
                if (index < PRESET_COLORS.length) {
                    // 前 20 个使用预设颜色
                    hubColorMap.set(hub.id, PRESET_COLORS[index]);
                } else {
                    // 超过 20 个使用随机颜色
                    hubColorMap.set(hub.id, generateRandomColor());
                }
            }
        });
        
        return (hubId: number): string => {
            return hubColorMap.get(hubId) || '#1890ff'; // 默认蓝色
        };
    }, [allWarehouses]);

    // 获取 site 对应的颜色
    const getColorForSite = useCallback((site: WarehouseType): string => {
        if (site.siteType === SiteType.HUB) {
            return getColorForHub(site.id!);
        } else {
            // Warehouse 使用其父 Hub 的颜色
            const parentHubId = site.parentSiteId;
            if (parentHubId) {
                return getColorForHub(parentHubId);
            }
            return '#1890ff'; // 默认蓝色
        }
    }, [getColorForHub]);
    
    // 处理 marker 点击，显示 InfoWindow
    const handleMarkerClick = useCallback((site: WarehouseType) => {
        if (!mapRef.current || !site.location?.latitude || !site.location?.longitude) return;
        
        // 关闭之前的 InfoWindow
        if (infoWindowRef.current) {
            infoWindowRef.current.close();
            infoWindowRef.current = null;
        }
        
        // 创建新的 InfoWindow
        const isHub = site.siteType === SiteType.HUB;
        const color = getColorForSite(site);
        
        // 创建标题元素（DOM Element，带圆角矩形背景）
        const headerElement = document.createElement('span');
        headerElement.style.display = 'inline-block';
        headerElement.style.backgroundColor = color;
        headerElement.style.color = 'white';
        headerElement.style.padding = '4px 12px';
        headerElement.style.borderRadius = '8px';
        headerElement.style.fontSize = '18px';
        headerElement.style.fontWeight = 'bold';
        headerElement.style.whiteSpace = 'nowrap';
        headerElement.textContent = site.name || (isHub ? `Hub ${site.id}` : `Warehouse ${site.id}`);
        
        // 创建内容
        const content = `
            <div style="display: inline-block; padding: 8px; line-height: normal;">
                <div style="font-size: 14px; margin-bottom: 8px; line-height: 1.5; color: #333333;">
                    <div style="margin-bottom: 4px;">ID: ${site.id || 'N/A'}</div>
                    <div>Manager: ${site.contactName || 'N/A'}</div>
                </div>
                <div style="font-size: 14px; display: flex; align-items: center; gap: 6px; color: #333333;">
                    <svg width="16" height="20" viewBox="0 0 32 40" xmlns="http://www.w3.org/2000/svg" style="flex-shrink: 0;">
                        <path d="M16 0C7.163 0 0 7.163 0 16c0 11.5 16 24 16 24s16-12.5 16-24C32 7.163 24.837 0 16 0z" fill="${color}"/>
                        <circle cx="16" cy="16" r="6" fill="white"/>
                    </svg>
                    <span>${site.location.latitude.toFixed(6)}, ${site.location.longitude.toFixed(6)}</span>
                </div>
            </div>
        `;
        
        const infoWindow = new google.maps.InfoWindow({
            content: content,
            headerContent: headerElement,
            maxWidth: 500
        });
        
        infoWindowRef.current = infoWindow;
        setSelectedSite(site);
        
        // 尝试使用 marker 作为 anchor，如果找不到则使用 position
        const marker = markerRefsRef.current.get(site.id || 0);
        if (marker) {
            infoWindow.open(mapRef.current, marker);
        } else {
            // 使用 position 打开 InfoWindow（fallback）
            infoWindow.open({
                map: mapRef.current,
                position: {
                    lat: site.location.latitude,
                    lng: site.location.longitude
                }
            } as google.maps.InfoWindowOpenOptions);
        }
        
        // 监听关闭事件
        google.maps.event.addListenerOnce(infoWindow, 'closeclick', () => {
            setSelectedSite(null);
            infoWindowRef.current = null;
        });
    }, [getColorForSite]);

    // 计算地图中心点和缩放级别
    const mapCenter = useMemo(() => {
        if (selectedWarehouses.length === 0) {
            return defaultCenter;
        }

        // 过滤出有坐标的仓库
        const warehousesWithLocation = selectedWarehouses.filter(
            w => w.location?.latitude && w.location?.longitude
        );

        if (warehousesWithLocation.length === 0) {
            return defaultCenter;
        }

        // 如果有多个仓库，计算中心点
        if (warehousesWithLocation.length === 1) {
            return {
                lat: warehousesWithLocation[0].location!.latitude,
                lng: warehousesWithLocation[0].location!.longitude
            };
        }

        // 计算所有选中仓库的中心点
        const avgLat = warehousesWithLocation.reduce(
            (sum, w) => sum + w.location!.latitude, 0
        ) / warehousesWithLocation.length;
        const avgLng = warehousesWithLocation.reduce(
            (sum, w) => sum + w.location!.longitude, 0
        ) / warehousesWithLocation.length;

        return { lat: avgLat, lng: avgLng };
    }, [selectedWarehouses]);

    // 默认缩放级别
    const mapZoom = useMemo(() => {
        if (selectedWarehouses.length === 0) {
            return 4; // 默认显示整个美国
        }
        return 10; // 初始缩放级别，会被 fitBounds 覆盖
    }, [selectedWarehouses]);

    // 当选中的 warehouse 变化时，调整地图边界
    useEffect(() => {
        if (!mapRef.current || selectedWarehouses.length === 0) {
            return;
        }

        // 过滤出有坐标的仓库和 Hub（只显示被选中的，不自动包含 Hub 下的 Warehouse）
        const sitesWithLocation = selectedWarehouses.filter(
            w => w.location?.latitude && w.location?.longitude
        );

        if (sitesWithLocation.length === 0) {
            return;
        }

        if (sitesWithLocation.length === 1) {
            // 单个站点时，居中显示并设置合适的缩放级别
            const location = sitesWithLocation[0].location!;
            mapRef.current.setCenter({
                lat: location.latitude,
                lng: location.longitude
            });
            mapRef.current.setZoom(12);
            return;
        }

        // 多个站点时，使用 fitBounds 显示所有被选中的标记
        const bounds = new google.maps.LatLngBounds();
        sitesWithLocation.forEach(site => {
            if (site.location?.latitude && site.location?.longitude) {
                bounds.extend({
                    lat: site.location.latitude,
                    lng: site.location.longitude
                });
            }
        });

        // 使用 fitBounds，并添加 padding 使标记占据 80% 的显示区域
        // padding 值越大，显示范围越小（标记占据的区域越大）
        // 计算 padding：如果希望标记占据 80%，则 padding 应该是 10%（每边 10%）
        // 获取地图容器的实际大小
        const mapDiv = mapRef.current.getDiv();
        const mapWidth = mapDiv.offsetWidth;
        const mapHeight = mapDiv.offsetHeight;
        const padding = Math.min(mapWidth, mapHeight) * 0.1; // 10% 的 padding，使内容占据 80%
        
        mapRef.current.fitBounds(bounds, {
            top: padding,
            right: padding,
            bottom: padding,
            left: padding
        });
    }, [selectedWarehouses]);

    // 地图加载完成时的回调
    const onMapLoad = (map: google.maps.Map) => {
        mapRef.current = map;
        
        // 如果已有选中的 warehouse，立即调整边界（只显示被选中的，不自动包含 Hub 下的 Warehouse）
        if (selectedWarehouses.length > 0) {
            const sitesWithLocation = selectedWarehouses.filter(
                w => w.location?.latitude && w.location?.longitude
            );

            if (sitesWithLocation.length > 0) {
                if (sitesWithLocation.length === 1) {
                    const location = sitesWithLocation[0].location!;
                    map.setCenter({
                        lat: location.latitude,
                        lng: location.longitude
                    });
                    map.setZoom(12);
                } else {
                    const bounds = new google.maps.LatLngBounds();
                    sitesWithLocation.forEach(site => {
                        if (site.location?.latitude && site.location?.longitude) {
                            bounds.extend({
                                lat: site.location.latitude,
                                lng: site.location.longitude
                            });
                        }
                    });
                    // 获取地图容器的实际大小
                    const mapDiv = map.getDiv();
                    const mapWidth = mapDiv.offsetWidth;
                    const mapHeight = mapDiv.offsetHeight;
                    const padding = Math.min(mapWidth, mapHeight) * 0.1; // 10% 的 padding，使内容占据 80%
                    map.fitBounds(bounds, {
                        top: padding,
                        right: padding,
                        bottom: padding,
                        left: padding
                    });
                }
            }
        }
    };

    // 创建 Marker icon（带动画效果）
    const createMarkerIcon = (color: string, isHub: boolean, withAnimation: boolean = false): google.maps.Icon => {
        const size = isHub ? { width: 32, height: 40 } : { width: 19, height: 24 }; // Warehouse 为 60%
        const anchor = isHub ? { x: 16, y: 40 } : { x: 9.5, y: 24 };
        
        // 添加动画效果的 SVG（使用 CSS animation）
        const animationClass = withAnimation ? 'marker-pulse' : '';
        const svg = `
            <svg width="${size.width}" height="${size.height}" viewBox="0 0 32 40" xmlns="http://www.w3.org/2000/svg" class="${animationClass}">
                <style>
                    .marker-pulse {
                        animation: markerPulse 1s ease-in-out infinite;
                    }
                    @keyframes markerPulse {
                        0% { transform: scale(1); opacity: 1; }
                        50% { transform: scale(1.2); opacity: 0.8; }
                        100% { transform: scale(1); opacity: 1; }
                    }
                </style>
                <g class="${animationClass}">
                    <path d="M16 0C7.163 0 0 7.163 0 16c0 11.5 16 24 16 24s16-12.5 16-24C32 7.163 24.837 0 16 0z" fill="${color}"/>
                    <circle cx="16" cy="16" r="6" fill="white"/>
                </g>
            </svg>
        `;
        
        // 使用 HTML 元素创建带动画的 marker
        if (withAnimation) {
            const div = document.createElement('div');
            div.innerHTML = svg;
            div.style.width = `${size.width}px`;
            div.style.height = `${size.height}px`;
            div.style.animation = 'markerPulse 1s ease-in-out infinite';
            
            // 创建 canvas 来生成图片（用于 Google Maps）
            const canvas = document.createElement('canvas');
            canvas.width = size.width;
            canvas.height = size.height;
            const ctx = canvas.getContext('2d');
            
            // 绘制基础 marker（动画效果通过 CSS 实现，但 Google Maps 不支持，所以使用定时更新）
            const img = new Image();
            img.onload = () => {
                ctx?.drawImage(img, 0, 0);
            };
            img.src = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                <svg width="${size.width}" height="${size.height}" viewBox="0 0 32 40" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 0C7.163 0 0 7.163 0 16c0 11.5 16 24 16 24s16-12.5 16-24C32 7.163 24.837 0 16 0z" fill="${color}"/>
                    <circle cx="16" cy="16" r="6" fill="white"/>
                </svg>
            `);
        }
        
        return {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                <svg width="${size.width}" height="${size.height}" viewBox="0 0 32 40" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 0C7.163 0 0 7.163 0 16c0 11.5 16 24 16 24s16-12.5 16-24C32 7.163 24.837 0 16 0z" fill="${color}"/>
                    <circle cx="16" cy="16" r="6" fill="white"/>
                </svg>
            `),
            scaledSize: { width: size.width, height: size.height } as google.maps.Size,
            anchor: { x: anchor.x, y: anchor.y } as google.maps.Point,
        };
    };
    
    // 使用自定义 HTML 元素创建带动画的 Marker
    const createAnimatedMarker = (color: string, isHub: boolean, position: { lat: number; lng: number }, title: string) => {
        const size = isHub ? { width: 32, height: 40 } : { width: 19, height: 24 };
        const anchor = isHub ? { x: 16, y: 40 } : { x: 9.5, y: 24 };
        
        // 创建带动画的 div
        const markerDiv = document.createElement('div');
        markerDiv.style.width = `${size.width}px`;
        markerDiv.style.height = `${size.height}px`;
        markerDiv.style.backgroundImage = `url(data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
            <svg width="${size.width}" height="${size.height}" viewBox="0 0 32 40" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 0C7.163 0 0 7.163 0 16c0 11.5 16 24 16 24s16-12.5 16-24C32 7.163 24.837 0 16 0z" fill="${color}"/>
                <circle cx="16" cy="16" r="6" fill="white"/>
            </svg>
        `)})`;
        markerDiv.style.backgroundSize = 'contain';
        markerDiv.style.backgroundRepeat = 'no-repeat';
        markerDiv.style.animation = 'markerPulse 1s ease-in-out infinite';
        markerDiv.style.transformOrigin = 'center bottom';
        
        return markerDiv;
    };

    if (loadError) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-red-500">无法加载地图，请检查网络连接</div>
            </div>
        );
    }

    return (
        <div className="flex h-full">
            <SitesMapTree
                onCheckboxChange={handleCheckboxChange}
                onWarehousesChange={handleWarehousesChange}
                getColorForSite={getColorForSite}
            />
            <div className="flex-1 relative">
                {!isLoaded ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="text-gray-500">正在加载地图...</div>
                    </div>
                ) : (
                    <GoogleMap
                        mapContainerStyle={{ width: '100%', height: '100%' }}
                        center={mapCenter}
                        zoom={mapZoom}
                        options={mapOptions}
                        onLoad={onMapLoad}
                    >
                        {/* 渲染选中的 Hub 和 Warehouse 标记 */}
                        {selectedWarehouses
                            .filter(w => w.location?.latitude && w.location?.longitude)
                            .map((site, index) => {
                                const isHub = site.siteType === SiteType.HUB;
                                const color = getColorForSite(site);
                                const title = isHub 
                                    ? `Hub: ${site.name || `Hub ${site.id}`}`
                                    : `Warehouse: ${site.name || `Warehouse ${site.id}`}`;
                                
                                return (
                                    <SiteMarker
                                        key={`${isHub ? 'hub' : 'warehouse'}-${site.id}`}
                                        position={{
                                            lat: site.location!.latitude,
                                            lng: site.location!.longitude
                                        }}
                                        color={color}
                                        isHub={isHub}
                                        title={title}
                                        zIndex={isHub ? 200 : 100}
                                        site={site}
                                        onMarkerClick={handleMarkerClick}
                                        onMarkerLoad={(marker, siteId) => {
                                            markerRefsRef.current.set(siteId, marker);
                                        }}
                                    />
                                );
                            })}
                        
                    </GoogleMap>
                )}
            </div>
        </div>
    );
};

export default SiteMapPage;
